/**
 * Formats d'un CV déposé à la main (page « CV à rattacher » et bouton de la
 * fiche). Le format est décidé sur les octets, jamais sur le nom ni sur le type
 * annoncé par le navigateur : la détection vit dans l'API
 * (`apps/api/src/cv/format-cv.ts`), ce module n'en porte que les constantes.
 */
export const FORMATS_CV = ['pdf', 'docx', 'doc', 'jpg', 'png'] as const;
export type FormatCv = (typeof FORMATS_CV)[number];

export const MIME_FORMAT_CV: Record<FormatCv, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  jpg: 'image/jpeg',
  png: 'image/png',
};

/** Un CV par requête, 10 Mo au plus (limite de multer et contrôle du navigateur). */
export const TAILLE_CV_MAX = 10 * 1024 * 1024;

/** Attribut `accept` du champ fichier : extensions et types, pour les sélecteurs des téléphones. */
export const ACCEPT_CV =
  '.pdf,.docx,.doc,.jpg,.jpeg,.png,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,image/jpeg,image/png';

/**
 * Bornes du contrôle d'un DOCX (archive ZIP) : décompression réelle, entrée par
 * entrée, sans jamais faire confiance aux tailles déclarées.
 */
export const LIMITES_DOCX = {
  entreesMax: 2000,
  /** Sortie réelle d'une entrée. */
  entreeMaxOctets: 25 * 1024 * 1024,
  /** Sortie réelle cumulée de toutes les entrées. */
  decompresseMaxOctets: 50 * 1024 * 1024,
  /** Rapport de compression au-delà duquel une grosse entrée est suspecte. */
  rapportMax: 200,
  seuilRapportOctets: 1024 * 1024,
} as const;

export const REFUS_FORMAT_CV = [
  'vide',
  'inconnu',
  'heic',
  'docx_invalide',
  'docx_suspect',
  'docx_macros',
  'docx_lien_externe',
  'docx_champ_externe',
  'doc_invalide',
  'doc_macros',
  'office_protege',
] as const;
export type RefusFormatCv = (typeof REFUS_FORMAT_CV)[number];

/** `lisible` : le worker sait en tirer du texte (tout sauf l'ancien `.doc`, en téléchargement seulement). */
export type FormatCvLu =
  | { ok: true; format: FormatCv; mime: string; lisible: boolean }
  | { ok: false; refus: RefusFormatCv };

export const MESSAGES_FORMAT_CV: Record<RefusFormatCv, string> = {
  vide: 'Fichier vide',
  inconnu: 'Formats acceptés : PDF, Word (.docx ou .doc), JPEG ou PNG',
  heic: 'Photo HEIC (iPhone) : enregistrez-la en JPEG ou déposez le CV en PDF',
  docx_invalide: 'Document Word illisible : enregistrez-le de nouveau en .docx ou en PDF',
  docx_suspect: 'Document Word refusé : contenu anormal une fois décompressé',
  docx_macros: 'Document Word avec macros : enregistrez-le en .docx sans macros ou en PDF',
  docx_lien_externe: 'Document Word lié à un contenu distant : enregistrez-le en PDF',
  docx_champ_externe:
    'Document Word dont un champ va chercher un contenu à l’extérieur (INCLUDETEXT, INCLUDEPICTURE, LINK, DDE…) : enregistrez-le en PDF',
  doc_invalide: 'Fichier Office qui n’est pas un document Word : enregistrez-le en PDF',
  doc_macros: 'Ancien document Word avec macros : enregistrez-le en PDF',
  office_protege: 'Document protégé par mot de passe : retirez la protection ou déposez un PDF',
};

/** Avertissement affiché à côté d'un `.doc` : il ne s'ouvre qu'en téléchargement. */
export const AVERTISSEMENT_DOC = 'Ancien format Word : ouvrir avec les macros désactivées';
